import React, { useState, useEffect } from "react";
import { Users, FileText, TrendingUp, Clock } from "lucide-react";
import { useLanguage } from '../context/LanguageContext';

const DmsDashboard = () => {
  const { language } = useLanguage();
  const BASE_URL = "https://santhai.kooturavu.tn.gov.in/api/v1";
  const AUTH_TOKEN = "Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9";

  const [metrics, setMetrics] = useState({
    clientDepartments: 0,
    totalOrders: 0,
    totalSalesValue: 0,
    medianDeliveryTime: 0,
    loading: true,
    error: null
  });

  useEffect(() => {
    fetchMetrics();
  }, []);

  const fetchMetrics = async () => {
    try {
      // Get current date and date range (last 30 days)
const endDate = new Date().toISOString().split('T')[0];
const startDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const response = await fetch(
        `${BASE_URL}/indent_data?start=${startDate}&end=${endDate}`,
        {
          method: 'GET',
          headers: {
            'Authorization': AUTH_TOKEN,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch data');
      }

      const result = await response.json();
      
      if (!result.error && result.data) {
        const indentData = result.data;

        // Calculate metrics
        const uniqueDepartments = new Set(indentData.map(item => item.department)).size;
        const totalOrders = indentData.length;
        const totalSales = indentData.reduce((sum, item) => sum + (item.dispatch_value || 0), 0);
        
        // Calculate median delivery time
        const deliveryTimes = indentData
          .map(item => item.delivered_days)
          .filter(days => days !== null && days !== undefined)
          .sort((a, b) => a - b);
        
        const median = deliveryTimes.length > 0
          ? deliveryTimes.length % 2 === 0
            ? (deliveryTimes[deliveryTimes.length / 2 - 1] + deliveryTimes[deliveryTimes.length / 2]) / 2
            : deliveryTimes[Math.floor(deliveryTimes.length / 2)]
          : 0;

        setMetrics({
          clientDepartments: uniqueDepartments,
          totalOrders: totalOrders,
          totalSalesValue: totalSales,
          medianDeliveryTime: median,
          loading: false,
          error: null
        });
      }
    } catch (error) {
      console.error('Error fetching metrics:', error);
      setMetrics(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to load metrics'
      }));
    }
  };

  const content = {
    en: {
      title: "Kooturavu Santhai",
    
   
      clientDepts: "Client Departments",
      totalOrders: "Total Orders",
      salesValue: "Total Sales Value",
      deliveryTime: "Median Delivery Time",
      days: "days",
      loading: "Loading metrics...",
      error: "Error loading data",
    },
    ta: {
      title: "கூட்டுறவு சந்தை",

   
      clientDepts: "வாடிக்கையாளர் துறைகள்",
      totalOrders: "மொத்த ஆர்டர்கள்",
      salesValue: "மொத்த விற்பனை மதிப்பு",
      deliveryTime: "சராசரி டெலிவரி நேரம்",
      days: "நாட்கள்",
      loading: "அளவீடுகள் ஏற்றப்படுகின்றன...",
      error: "தரவை ஏற்ற முடியவில்லை",
    }
  };

  const t = content[language];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatNumber = (value) => {
    return new Intl.NumberFormat('en-IN').format(value);
  };

  if (metrics.loading) {
    return (
      <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen py-8 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-700">{t.loading}</p>
        </div>
      </div>
    );
  }

  if (metrics.error) {
    return (
      <div className="w-full bg-gradient-to-br from-slate-50 to-red-50 min-h-screen py-8 flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-red-600">{t.error}</p>
          <button 
            onClick={fetchMetrics}
            className="mt-4 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen py-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <h1 className="text-4xl font-bold text-center text-gray-900 mb-2">
          {t.title}
        </h1>
        <p className="text-center text-gray-600 text-lg">{t.subtitle}</p>
        <p className="text-center text-sm text-gray-500 mt-2">{t.lastUpdated}</p>
      </div>

      {/* Key Metrics Cards */}
      <div className="max-w-7xl mx-auto px-6 mb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          
          {/* Client Departments */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-blue-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
              {t.clientDepts}
            </h3>
            <p className="text-4xl font-bold text-gray-900">
              {formatNumber(metrics.clientDepartments)}
            </p>
          </div>

          {/* Total Orders */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-green-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
                <FileText className="w-8 h-8 text-green-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
              {t.totalOrders}
            </h3>
            <p className="text-4xl font-bold text-gray-900">
              {formatNumber(metrics.totalOrders)}
            </p>
          </div>

          {/* Total Sales Value */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-purple-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center">
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
              {t.salesValue}
            </h3>
            <p className="text-3xl font-bold text-gray-900">
              {formatCurrency(metrics.totalSalesValue)}
            </p>
          </div>

          {/* Median Delivery Time */}
          <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 border-t-4 border-amber-500">
            <div className="flex items-center justify-between mb-4">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
            </div>
            <h3 className="text-gray-600 text-sm font-semibold mb-2 uppercase tracking-wide">
              {t.deliveryTime}
            </h3>
            <div className="flex items-baseline gap-2">
              <p className="text-4xl font-bold text-gray-900">
                {metrics.medianDeliveryTime.toFixed(1)}
              </p>
              <span className="text-lg text-gray-600 font-medium">{t.days}</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default DmsDashboard;